import { Link, useParams } from 'react-router-dom';
import useSWR from 'swr';
import { format, parseISO } from 'date-fns';
import { ChevronLeft, Loader2 } from 'lucide-react';
import axiosInstance from '@/lib/axios';
import { formatDisplayPhone } from '@/lib/phone-utils';
import { EventsPageShell } from '../components/EventsPageShell';

type Recipient = {
  name: string;
  phone: string;
  status: string;
  sentAt: string;
  deliveredAt: string | null;
  readAt: string | null;
  errorReason: string | null;
};

type BatchDetail = {
  batch: {
    batchId: string;
    reason: string;
    audience: string;
    eventTitle: string;
    messageType: string;
    templateName: string;
  };
  recipients: Recipient[];
};

const STATUS_LABEL: Record<string, string> = {
  SENT: 'Sent',
  DELIVERED: 'Delivered',
  READ: 'Read',
  FAILED: 'Failed',
};

function formatWhen(value: string | null) {
  if (!value) return '—';
  const date = parseISO(value);
  if (Number.isNaN(date.getTime())) return value;
  return format(date, 'PPP p');
}

export default function EventMarketingBatchPage() {
  const { communityId, eventId, batchId } = useParams();
  const { data, isLoading, error } = useSWR<BatchDetail>(
    communityId && eventId && batchId
      ? `/api/communities/${communityId}/events/${eventId}/marketing/batches/${encodeURIComponent(batchId)}`
      : null,
    (url: string) => axiosInstance.get(url).then((res) => res.data)
  );

  return (
    <EventsPageShell>
      <div className="space-y-6 px-4 py-6 md:px-0">
        <Link
          to={`/${communityId}/events/${eventId}/marketing`}
          className="inline-flex items-center text-sm text-muted-foreground hover:text-primary"
        >
          <ChevronLeft className="mr-1 h-4 w-4" />
          Back to marketing
        </Link>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">
            {data?.batch.eventTitle || 'Message details'}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {[data?.batch.reason, data?.batch.templateName].filter(Boolean).join(' · ')}
          </p>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : error || !data ? (
          <p className="text-sm text-muted-foreground">This send could not be found.</p>
        ) : (
          <div className="space-y-3">
            {data.recipients.map((person) => (
              <article
                key={`${person.phone}-${person.sentAt}`}
                className="rounded-2xl bg-card p-5 shadow-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-base font-semibold">{person.name}</h2>
                    <p className="text-sm text-muted-foreground">
                      {formatDisplayPhone(person.phone)}
                    </p>
                  </div>
                  <span className="text-sm font-medium">
                    {STATUS_LABEL[person.status] || person.status}
                  </span>
                </div>
                <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-3">
                  <div>
                    <dt className="text-xs text-muted-foreground">Sent</dt>
                    <dd>{formatWhen(person.sentAt)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Delivered</dt>
                    <dd>{formatWhen(person.deliveredAt)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Read</dt>
                    <dd>{formatWhen(person.readAt)}</dd>
                  </div>
                </dl>
                {person.errorReason ? (
                  <p className="mt-3 text-sm text-foreground">{person.errorReason}</p>
                ) : null}
              </article>
            ))}
          </div>
        )}
      </div>
    </EventsPageShell>
  );
}
